import { RateCalendarStore } from "stores/rateCalendar/RateCalendarStore";
import { SerializedData, Store } from "bernie-core";
import { DatePickerContent } from "src/components/flexComponents/WizardHotelPWA/typings";
import { RateCalendarFooterProps } from "components/flexComponents/WizardHotelPWA/components/RateCalendarFooter";
import { DestinationPricingService } from "services/DestinationPricingService";
import { Logger, NOOP_LOGGER, SystemEvent, SystemEventLevel } from "bernie-logger";
import { ExtendedContextStore, FlexContext } from "typings/flexFramework/FlexDefinitions";
import { Experiment, getExperimentBucket } from "components/utility/experiment";
import { ILocalization } from "bernie-l10n";
import { Hotel, HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";
import { action } from "mobx";
import { FlexModuleModelStore } from "stores/FlexModuleModelStore";

const NUMBER_OF_HOTELS_THRESHOLD = 20;

export class ServerRateCalendarStore extends Store implements RateCalendarStore {
  public experimentBucket: number;

  public isExperimentEnabled: boolean;

  public datePickerContent: DatePickerContent;

  public footerHtmlProps: RateCalendarFooterProps;

  public footerText = "";

  public destinationPricingService: DestinationPricingService;

  public l10n: ILocalization;

  constructor(
    destinationPricingService: DestinationPricingService,
    l10n: ILocalization,
    state: SerializedData = {},
    logger: Logger = NOOP_LOGGER
  ) {
    super(state, logger);
    this.destinationPricingService = destinationPricingService;
    this.l10n = l10n;
  }

  public hydrate(data: SerializedData): void {
    // does nothing
  }

  /**
   * removes data that will be re-constructed client-side
   * @returns SerializedData
   */
  public toJSON(): SerializedData {
    return {
      experimentBucket: this.experimentBucket,
      isExperimentEnabled: this.isExperimentEnabled,
      datePickerContent: this.datePickerContent,
      footerHtmlProps: this.footerHtmlProps,
      footerText: this.footerText,
    };
  }

  /**
   * Initializes RateCalendarStore with data from other stores.
   * @param  { FlexContext } flexContext
   * @param  { ExtendedContextStore } contextStore
   * @param flexModuleModelStore
   * @returns void
   */
  @action
  public setup(
    flexContext: FlexContext,
    contextStore: ExtendedContextStore,
    flexModuleModelStore: FlexModuleModelStore
  ) {
    this.setExperimentBucket(contextStore);
    this.isExperimentEnabled = this.experimentBucket !== 0;

    if (!flexContext || !this.isExperimentEnabled || !flexModuleModelStore.modelMap) {
      return;
    }

    const hotels = this.findHotels(flexModuleModelStore);

    if (!hotels) {
      return;
    }

    if (hotels.length < NUMBER_OF_HOTELS_THRESHOLD) {
      this.logger.logEvent(new SystemEvent(SystemEventLevel.WARN, "RATE_CALENDAR_SETUP"), "Not enough hotels.");
      return;
    }

    // Set upper limit to number of hotels
    const limitedHotels = hotels.slice(0, NUMBER_OF_HOTELS_THRESHOLD);

    return this.destinationPricingService
      .fetchRateCalendar(limitedHotels, flexContext, contextStore)
      .then((response) => {
        if (response === undefined) {
          this.logger.logEvent(
            new SystemEvent(SystemEventLevel.WARN, "RATE_CALENDAR_SETUP"),
            "Empty response from DestinationPricingService."
          );

          return;
        }

        this.setDatePickerContent(response);
        this.setFooterHtmlProps();
        this.setFooterText();

        return response;
      })
      .catch((error) => this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, "RATE_CALENDAR_SETUP"), error));
  }

  public setFooterHtmlProps() {
    const props = { displayPositive: false, displayNegative: false };

    if (this.datePickerContentIsEmpty() || !this.isExperimentEnabled) {
      this.footerHtmlProps = props;

      return;
    }

    if (this.experimentBucket === 1) {
      props.displayNegative = true;
    } else if (this.experimentBucket === 2) {
      props.displayPositive = true;
    } else if (this.experimentBucket === 3) {
      props.displayNegative = true;
      props.displayPositive = true;
    }

    this.footerHtmlProps = props;
  }

  public setFooterText() {
    if (this.experimentBucket !== 0 && !this.datePickerContentIsEmpty()) {
      this.footerText = this.l10n.formatText("wizard.ratecalendar.footer.legend.legal", 20);
    }
  }

  private setExperimentBucket(contextStore: ExtendedContextStore) {
    this.experimentBucket = getExperimentBucket(
      contextStore,
      Experiment.Destination_Pricing_Rate_Calendar_HTG_Filter_Wizard
    );
  }

  private setDatePickerContent(datePickerContent: DatePickerContent) {
    this.datePickerContent = datePickerContent;
  }

  private datePickerContentIsEmpty(): boolean {
    return !this.datePickerContent || Object.keys(this.datePickerContent).length === 0;
  }

  public findHotels(flexModuleModelStore: FlexModuleModelStore): Hotel[] | null {
    const hotelsModel = flexModuleModelStore.findFirstByType("hotels");

    if (hotelsModel) {
      return (hotelsModel.model as HotelsFlexModuleResult).hotels;
    }

    return null;
  }
}
